import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Shield, MessageCircle, Users, Star, ChevronDown, ChevronRight, ChevronLeft, Sparkles, Check, X, ArrowRight, Phone, BadgeCheck, MessageSquare, Download } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const APP_STORE_URL = "https://apps.apple.com/app/id6757411835";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.recoverymatch.dating";

const getDevicePlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
};

const SmartDownloadButton = ({ className = "", size = "default" }) => {
  const platform = getDevicePlatform();
  const height = size === "large" ? "h-16" : "h-14";

  if (platform === 'ios') {
    return (
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className={`hover:scale-105 transition-transform ${className}`} data-testid="smart-download-btn">
        <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on App Store" className={height} />
      </a>
    );
  }
  if (platform === 'android') {
    return (
      <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className={`hover:scale-105 transition-transform ${className}`} data-testid="smart-download-btn">
        <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className={height} />
      </a>
    );
  }
  // Desktop: show both
  return (
    <div className={`flex flex-wrap items-center gap-4 ${className}`}>
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-transform" data-testid="app-store-badge">
        <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on App Store" className={height} />
      </a>
      <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-transform" data-testid="google-play-badge">
        <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className={height} />
      </a>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [currentStory, setCurrentStory] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState({});
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showFloatingBanner, setShowFloatingBanner] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const heroRef = useRef(null);
  const API_URL = "https://api.recoverymatch.com";

  // Track mouse for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left - rect.width / 2) / 50,
          y: (e.clientY - rect.top - rect.height / 2) / 50,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Show floating banner after scrolling past hero section
  useEffect(() => {
    const handleScroll = () => {
      if (bannerDismissed) return;
      const scrollY = window.scrollY;
      const heroHeight = heroRef.current?.offsetHeight || 600;
      setShowFloatingBanner(scrollY > heroHeight * 0.7);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [bannerDismissed]);

  const successStories = [
    {
      names: "Sarah & Michael",
      image: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400&h=500&fit=crop",
      quote: "We were matched based on our values and recovery journeys. Three years sober together and counting.",
      duration: "Together 2 years"
    },
    {
      names: "James & Emily", 
      image: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400&h=500&fit=crop",
      quote: "Finding someone who understands recovery made all the difference. We got engaged last month!",
      duration: "Together 18 months"
    },
    {
      names: "David & Chris",
      image: "https://images.unsplash.com/photo-1516914943479-89db7d9ae7f2?w=400&h=500&fit=crop",
      quote: "RecoveryMatch gave us the safe space to be honest about our journeys from day one.",
      duration: "Together 14 months"
    }
  ];

  const features = [
    { icon: <Heart className="w-7 h-7" />, title: "Curated Matches", description: "Daily matches based on deep compatibility" },
    { icon: <Shield className="w-7 h-7" />, title: "Recovery-Focused", description: "Profiles built for the recovery community" },
    { icon: <MessageCircle className="w-7 h-7" />, title: "Guided Conversations", description: "Meaningful prompts to start real connections" },
    { icon: <Sparkles className="w-7 h-7" />, title: "AI Bio Assistant", description: "Let AI craft your perfect profile bio", isNew: true },
    { icon: <MessageSquare className="w-7 h-7" />, title: "AI Conversation Starters", description: "Never run out of things to say", isNew: true },
    { icon: <Star className="w-7 h-7" />, title: "Compatibility Score", description: "See how well you align with matches" }
  ];

  const faqs = [
    { q: "Who is RecoveryMatch for?", a: "RecoveryMatch is for adults (18+) in recovery from alcohol and substance use who want meaningful romantic connections with people who understand their journey." },
    { q: "Is my recovery information private?", a: "Yes! You control what you share. Your sobriety date and program details are only visible to other members, and you can adjust privacy settings anytime." },
    { q: "How does matching work?", a: "We use a detailed compatibility algorithm based on your recovery journey, values, and relationship goals. You receive curated daily matches (3-5 per day) with compatibility scores. Express interest, and if it's mutual, you can start chatting!" },
    { q: "Is RecoveryMatch free?", a: "Yes! Basic features are free with 3 matches per day. Recovery+ ($9.99/mo) lets you see who liked you and like them back instantly. Recovery Elite ($19.99/mo) adds Super Likes, video calls, and advanced filters." }
  ];

  const nextStory = () => setCurrentStory((prev) => (prev + 1) % successStories.length);
  const prevStory = () => setCurrentStory((prev) => (prev - 1 + successStories.length) % successStories.length);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="landing-page">
      
      {/* Navigation - Navy Blue */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch Logo" 
              className="w-10 h-10 rounded-xl object-cover"
            />
            <span className="text-xl font-bold">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-[#FAB710] to-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Light with Navy/Orange accents */}
      <section 
        ref={heroRef}
        className="min-h-screen relative flex items-center justify-center overflow-hidden bg-gradient-to-b from-blue-50 via-white to-orange-50/30 pt-12"
      >
        {/* Subtle decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute w-[500px] h-[500px] rounded-full opacity-10 blur-[80px]"
            style={{
              background: 'linear-gradient(135deg, #1C3A7A, #3B5998)',
              top: '5%',
              left: '5%',
              transform: `translate(${mousePosition.x * 1.5}px, ${mousePosition.y * 1.5}px)`,
              transition: 'transform 0.3s ease-out'
            }}
          />
          <div 
            className="absolute w-[400px] h-[400px] rounded-full opacity-10 blur-[60px]"
            style={{
              background: 'linear-gradient(135deg, #FAB710, #FAB710)',
              bottom: '10%',
              right: '5%',
              transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`,
              transition: 'transform 0.3s ease-out'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 pt-16 pb-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            
            {/* Left Content */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#1C3A7A]/5 border border-[#1C3A7A]/10 px-4 py-2 rounded-full text-sm mb-6">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-[#1C3A7A]/70 font-medium">10,000+ recovery connections made</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 text-[#1C3A7A]">
                Find Love{" "}
                <span className="whitespace-nowrap">in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FAB710] to-[#FAB710]">Recovery</span></span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-lg mx-auto lg:mx-0">
                Connect with people who truly understand your journey. Build meaningful relationships in a safe, supportive community.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={() => navigate('/register')}
                  className="group flex items-center justify-center gap-2 bg-gradient-to-r from-[#FAB710] to-[#FAB710] text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <a 
                  href="#stories"
                  className="flex items-center justify-center gap-2 border-2 border-[#1C3A7A] text-[#1C3A7A] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#1C3A7A] hover:text-white transition-all duration-300"
                >
                  See Success Stories
                </a>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 mt-10 justify-center lg:justify-start text-gray-500 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#1C3A7A]" />
                  <span>Verified Profiles</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-[#FAB710]" />
                  <span>Recovery First</span>
                </div>
              </div>
            </div>

            {/* Right - Interactive Card Stack */}
            <div className="flex-1 relative h-[520px] w-full max-w-[420px]">
              {/* Background cards */}
              <div 
                className="absolute left-1/2 top-1/2 w-[280px] h-[380px] rounded-3xl bg-[#1C3A7A]/10 border border-[#1C3A7A]/20"
                style={{
                  transform: `translate(-50%, -50%) rotate(-8deg) translate(${mousePosition.x * -0.5}px, ${mousePosition.y * -0.5}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              />
              <div 
                className="absolute left-1/2 top-1/2 w-[280px] h-[380px] rounded-3xl bg-orange-100 border border-orange-200"
                style={{
                  transform: `translate(-50%, -50%) rotate(4deg) translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              />
              
              {/* Main card */}
              <div 
                className="absolute left-1/2 top-1/2 w-[300px] h-[400px] rounded-3xl overflow-hidden shadow-2xl shadow-[#1C3A7A]/20 bg-white"
                style={{
                  transform: `translate(-50%, -50%) translate(${mousePosition.x}px, ${mousePosition.y}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=600&fit=crop&crop=face" 
                  alt="Profile"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C3A7A]/90 via-[#1C3A7A]/20 to-transparent" />
                
                {/* Card content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-green-500/90 text-white text-xs px-3 py-1 rounded-full font-medium">
                      2 years sober
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold">Sarah, 28</h3>
                  <p className="text-white/80 text-sm">New York, NY</p>
                </div>

                {/* Action buttons overlay */}
                <div className="absolute bottom-6 right-6 flex gap-3">
                  <button className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-red-50 hover:scale-110 transition-all border border-gray-100">
                    <X className="w-5 h-5 text-red-500" />
                  </button>
                  <button className="w-12 h-12 rounded-full bg-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg border border-gray-100 hover:bg-green-50">
                    <Check className="w-5 h-5 text-green-500" />
                  </button>
                </div>
              </div>

              {/* Floating match notification */}
              <div 
                className="absolute -right-2 top-16 bg-white border border-gray-100 rounded-2xl p-4 shadow-xl animate-bounce"
                style={{ animationDuration: '3s' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden">
                    <img src="/images/logo-main.png" alt="Match" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1C3A7A] text-sm">It's a Match!</p>
                    <p className="text-gray-500 text-xs">You and Sarah liked each other</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
          <span className="text-gray-400 text-sm">Scroll to explore</span>
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </div>
      </section>

      {/* Trust Banner - Navy Blue */}
      <section className="py-6 bg-[#1C3A7A]">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-center gap-8 text-white">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            <span className="font-medium">Verified Profiles</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            <span className="font-medium">10,000+ Success Stories</span>
          </div>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#FAB710]" />
            <span className="font-medium">Recovery First Community</span>
          </div>
        </div>
      </section>

      {/* 📱 Download the App Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-50 via-white to-orange-50/50" data-testid="app-download-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2A4A8A] rounded-3xl p-8 md:p-12 overflow-hidden relative">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#FAB710]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
            
            <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
              {/* Left Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-[#FAB710]/20 text-[#FAB710] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Phone className="w-4 h-4" />
                  Available Now
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Take RecoveryMatch{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FAB710] to-yellow-300">
                    With You
                  </span>
                </h2>
                <p className="text-white/70 text-lg mb-6">
                  Get daily matches, chat with connections, and build meaningful relationships — all from your phone.
                </p>
                
                {/* Smart Download Button */}
                <div className="flex flex-wrap items-center gap-4 justify-center md:justify-start">
                  <SmartDownloadButton />
                </div>

                {/* Features Pills */}
                <div className="flex flex-wrap gap-2 mt-6 justify-center md:justify-start">
                  <span className="bg-white/10 text-white/80 text-sm px-3 py-1.5 rounded-full">Daily Matches</span>
                  <span className="bg-white/10 text-white/80 text-sm px-3 py-1.5 rounded-full">Secure Chat</span>
                  <span className="bg-white/10 text-white/80 text-sm px-3 py-1.5 rounded-full">Video Calls</span>
                </div>
              </div>

              {/* Right - Phone Mockup */}
              <div className="flex-shrink-0 hidden md:block">
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-[#FAB710]/20 rounded-[40px] blur-2xl scale-110" />
                  
                  {/* Phone Frame */}
                  <div className="relative w-[200px] h-[400px] bg-black rounded-[40px] p-2 shadow-2xl">
                    {/* Notch */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-5 bg-black rounded-full z-10" />
                    
                    {/* Screen */}
                    <div className="w-full h-full bg-gray-100 rounded-[34px] overflow-hidden">
                      <img 
                        src="/images/app-daily-matches.png" 
                        alt="RecoveryMatch App Screenshot"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Carousel */}
      <section id="stories" className="py-24 px-4 bg-gradient-to-b from-white to-blue-50/50">
        <div className="max-w-6xl mx-auto">
          <div 
            id="stories-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ${isVisible['stories-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-[#1C3A7A]">
              Real{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FAB710] to-[#FAB710]">
                Love Stories
              </span>
            </h2>
            <p className="text-gray-600 text-lg">People who found their person on RecoveryMatch</p>
          </div>

          <div className="relative">
            {/* Story Card */}
            <div className="flex items-center justify-center gap-8">
              <button 
                onClick={prevStory}
                className="hidden md:flex w-14 h-14 rounded-full bg-white border border-gray-200 shadow-sm items-center justify-center hover:bg-gray-50 hover:border-[#1C3A7A]/30 transition text-[#1C3A7A]"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <div className="relative w-full max-w-3xl">
                <div className="bg-white rounded-3xl border border-gray-100 p-8 md:p-12 shadow-xl shadow-[#1C3A7A]/5">
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    {/* Image */}
                    <div className="w-48 h-60 rounded-2xl overflow-hidden flex-shrink-0 shadow-lg">
                      <img 
                        src={successStories[currentStory].image}
                        alt={successStories[currentStory].names}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 text-center md:text-left">
                      <div className="flex items-center gap-2 justify-center md:justify-start mb-4">
                        <Heart className="w-5 h-5 text-[#FAB710] fill-[#FAB710]" />
                        <span className="text-[#FAB710] font-semibold">{successStories[currentStory].duration}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-[#1C3A7A] mb-4">{successStories[currentStory].names}</h3>
                      <p className="text-gray-600 text-lg italic">"{successStories[currentStory].quote}"</p>
                    </div>
                  </div>

                  {/* Dots indicator */}
                  <div className="flex justify-center gap-2 mt-8">
                    {successStories.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentStory(idx)}
                        className={`h-2 rounded-full transition-all ${idx === currentStory ? 'w-8 bg-gradient-to-r from-[#FAB710] to-[#FAB710]' : 'w-2 bg-gray-300'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <button 
                onClick={nextStory}
                className="hidden md:flex w-14 h-14 rounded-full bg-white border border-gray-200 shadow-sm items-center justify-center hover:bg-gray-50 hover:border-[#1C3A7A]/30 transition text-[#1C3A7A]"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div 
            id="features-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ${isVisible['features-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-[#1C3A7A]">
              Everything You Need
            </h2>
            <p className="text-gray-600 text-lg">Built for the recovery community</p>
          </div>

          <div 
            id="features-grid"
            data-animate
            className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 delay-200 ${isVisible['features-grid'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`group bg-white p-6 rounded-2xl border ${feature.isNew ? 'border-[#FAB710]/50 ring-2 ring-[#FAB710]/20' : 'border-gray-100'} hover:border-[#FAB710]/30 hover:shadow-xl hover:shadow-orange-100 hover:scale-105 transition-all duration-300 cursor-pointer relative`}
              >
                {feature.isNew && (
                  <div className="absolute -top-3 -right-2 bg-gradient-to-r from-[#FAB710] to-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    NEW ✨
                  </div>
                )}
                <div className={`w-14 h-14 ${feature.isNew ? 'bg-gradient-to-br from-[#FAB710]/20 to-yellow-400/20' : 'bg-gradient-to-br from-[#FAB710]/10 to-[#FAB710]/10'} rounded-xl flex items-center justify-center text-[#FAB710] mb-4 group-hover:scale-110 group-hover:bg-gradient-to-br group-hover:from-[#FAB710]/20 group-hover:to-[#FAB710]/20 transition-all`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1C3A7A] mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots Carousel */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-blue-50/50 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-[#1C3A7A]">
              See the App in Action
            </h2>
            <p className="text-gray-600 text-lg">A glimpse of your journey to meaningful connections</p>
          </div>

          {/* Phone Mockup Slider */}
          <div className="relative">
            {/* Navigation Arrows */}
            <button 
              onClick={() => {
                const container = document.getElementById('screenshot-slider');
                container.scrollBy({ left: -260, behavior: 'smooth' });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 hover:scale-110 transition-all border border-gray-200"
            >
              <ChevronLeft className="w-6 h-6 text-[#1C3A7A]" />
            </button>
            
            <button 
              onClick={() => {
                const container = document.getElementById('screenshot-slider');
                container.scrollBy({ left: 260, behavior: 'smooth' });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 hover:scale-110 transition-all border border-gray-200"
            >
              <ChevronRight className="w-6 h-6 text-[#1C3A7A]" />
            </button>

            {/* Slider Container */}
            <div 
              id="screenshot-slider"
              className="flex gap-6 overflow-x-auto scrollbar-hide px-12 py-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {[
                { title: "Daily Matches", desc: "Curated matches based on compatibility", image: "/images/app-daily-matches.png" },
                { title: "Profile View", desc: "Recovery-focused prompts & photos", image: "/images/app-profile-view.png" },
                { title: "Guided Chat", desc: "Meaningful conversations start here", image: "/images/app-guided-chat.png" },
                { title: "Video Calls", desc: "Connect face-to-face (Elite)", image: "/images/app-video-calls.png" },
                { title: "Compatibility", desc: "See how well you align", image: "/images/app-compatibility.png" },
                { title: "Conversations", desc: "All your connections in one place", image: "/images/app-conversations.png" },
                { title: "Referrals", desc: "Invite friends & earn rewards", image: "/images/app-referrals.png" },
                { title: "Recovery Journey", desc: "Celebrate milestones together", image: "/images/app-recovery-journey.jpg" },
                { title: "It's a Match!", desc: "Celebrate your new connection", image: "/images/app-its-a-match.png" },
              ].map((screen, index) => {
                return (
                  <div 
                    key={index}
                    className="flex flex-col items-center group flex-shrink-0 snap-center"
                  >
                    {/* Phone Frame */}
                    <div className="relative w-[160px] h-[380px] bg-[#1C3A7A] rounded-[32px] p-2 shadow-2xl shadow-[#1C3A7A]/20 group-hover:scale-105 group-hover:shadow-[#1C3A7A]/30 transition-all duration-300">
                      {/* Notch */}
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-14 h-4 bg-[#1C3A7A] rounded-full z-10" />
                      
                      {/* Screen */}
                      <div 
                        className="w-full h-full bg-gray-100 rounded-[26px] overflow-hidden relative cursor-pointer"
                        onClick={() => setLightboxImage({ src: screen.image, title: screen.title })}
                      >
                        <img 
                          src={screen.image} 
                          alt={screen.title}
                          className="w-full h-full object-cover object-top"
                        />
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-all bg-white/90 px-3 py-1 rounded-full text-xs font-medium text-[#1C3A7A]">
                            Click to enlarge
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Label */}
                    <div className="mt-4 text-center">
                      <h3 className="font-bold text-[#1C3A7A]">{screen.title}</h3>
                      <p className="text-gray-500 text-sm max-w-[180px]">{screen.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Scroll Indicator Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((_, idx) => (
                <div
                  key={idx}
                  className="w-2 h-2 rounded-full bg-[#1C3A7A]/20"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How Matching Works */}
      <section id="how-it-works" className="py-24 px-4 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-[#1C3A7A]">
              How <span className="text-[#FAB710]">Matching</span> Works
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              No endless swiping. Just meaningful connections curated for your recovery journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 h-full">
                <div className="w-12 h-12 bg-[#1C3A7A] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">1</div>
                <h3 className="text-xl font-bold text-[#1C3A7A] mb-3">Complete Your Profile</h3>
                <p className="text-gray-600">Tell us about your recovery journey, preferences, and what you're looking for in a partner.</p>
              </div>
              <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-[#FAB710] text-2xl">→</div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 h-full">
                <div className="w-12 h-12 bg-[#1C3A7A] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">2</div>
                <h3 className="text-xl font-bold text-[#1C3A7A] mb-3">Get Curated Matches</h3>
                <p className="text-gray-600">Our algorithm finds compatible people based on recovery program, location, age, and shared values.</p>
              </div>
              <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-[#FAB710] text-2xl">→</div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 h-full">
                <div className="w-12 h-12 bg-[#1C3A7A] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">3</div>
                <h3 className="text-xl font-bold text-[#1C3A7A] mb-3">Express Interest</h3>
                <p className="text-gray-600">Like someone? Let them know! When both of you express interest, it's a mutual match.</p>
              </div>
              <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-[#FAB710] text-2xl">→</div>
            </div>

            {/* Step 4 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 h-full">
                <div className="w-12 h-12 bg-[#FAB710] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[#1C3A7A] mb-3">Start Connecting</h3>
                <p className="text-gray-600">Once matched, start a conversation. Our AI can even help you with the perfect opener!</p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 bg-[#1C3A7A]/10 text-[#1C3A7A] px-6 py-3 rounded-full">
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">Quality over quantity — every match is thoughtfully curated</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-[#1C3A7A]">Questions?</h2>
            <p className="text-gray-600 text-lg">We've got answers</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-lg text-[#1C3A7A] pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FAB710] transition-transform ${openFaq === index ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-40' : 'max-h-0'}`}>
                  <div className="px-6 pb-6 text-gray-600">
                    {faq.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - Navy Blue */}
      <section className="py-24 px-4 bg-[#1C3A7A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FAB710]/10 blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative text-white">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Ready to Find{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FAB710] to-[#FAB710]">
              Your Match?
            </span>
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Join thousands of people in recovery who've found meaningful connections
          </p>
          
          <button 
            onClick={() => navigate('/register')}
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#FAB710] to-[#FAB710] text-white px-10 py-5 rounded-full font-semibold text-xl hover:shadow-2xl hover:shadow-orange-500/30 hover:scale-105 transition-all duration-300"
          >
            Get Started Free
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Smart Download Button */}
          <div className="flex items-center gap-4 mt-10 justify-center">
            <SmartDownloadButton />
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating Download App Banner */}
      {showFloatingBanner && !bannerDismissed && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-40 animate-in slide-in-from-bottom duration-500"
          data-testid="floating-app-banner"
        >
          {/* Gradient fade effect at top */}
          <div className="h-4 bg-gradient-to-t from-[#1C3A7A] to-transparent" />
          
          <div className="bg-[#1C3A7A] px-4 py-4 shadow-2xl shadow-black/30">
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
              {/* Left side - Text & Icon */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="hidden sm:flex w-12 h-12 bg-[#FAB710] rounded-xl items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6 text-[#1C3A7A]" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-bold text-sm sm:text-base truncate">Get the RecoveryMatch App</p>
                  <p className="text-white/70 text-xs sm:text-sm hidden sm:block">Daily matches & connections on the go</p>
                </div>
              </div>

              {/* Center - Smart Download Button */}
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <SmartDownloadButton />
              </div>

              {/* Right side - Close button */}
              <button 
                onClick={() => setBannerDismissed(true)}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-all flex-shrink-0"
                aria-label="Dismiss banner"
                data-testid="floating-banner-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAuthModal(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-md w-full p-8 relative animate-in fade-in zoom-in duration-300 shadow-2xl"
            style={{ animationDuration: '200ms' }}
          >
            {/* Close Button */}
            <button 
              onClick={() => setShowAuthModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="/images/logo-main.png" 
                alt="RecoveryMatch Logo" 
                className="w-16 h-16 rounded-2xl shadow-lg"
              />
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold text-center text-[#1C3A7A] mb-4">
              Create account
            </h2>

            {/* Legal Text */}
            <p className="text-center text-gray-600 text-sm mb-6 leading-relaxed">
              By tapping Log In or Continue, you agree to our{" "}
              <button onClick={() => { setShowAuthModal(false); navigate("/terms"); }} className="text-[#1C3A7A] underline hover:text-[#FAB710]">
                Terms
              </button>
              . Learn how we process your data in our{" "}
              <button onClick={() => { setShowAuthModal(false); navigate("/privacy"); }} className="text-[#1C3A7A] underline hover:text-[#FAB710]">
                Privacy Policy
              </button>
              , and{" "}
              <button onClick={() => { setShowAuthModal(false); navigate("/cookies"); }} className="text-[#1C3A7A] underline hover:text-[#FAB710]">
                Cookie Policy
              </button>
              .
            </p>

            {/* Auth Buttons */}
            <div className="space-y-3 mb-6">
              {/* Google Button */}
              <button 
                onClick={() => {
                  setShowAuthModal(false);
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + '/matches';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 bg-[#4285F4] hover:bg-[#357ABD] text-white py-4 px-6 rounded-full font-semibold transition-all hover:shadow-lg"
              >
                <svg className="w-6 h-6 bg-white rounded-full p-1" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              {/* Facebook - Coming Soon (waiting for custom domain) */}
              <button 
                disabled
                className="w-full flex items-center justify-center gap-3 bg-gray-100 border-2 border-gray-200 text-gray-400 py-4 px-6 rounded-full font-semibold cursor-not-allowed"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#9CA3AF">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook — Coming Soon
              </button>

              {/* Phone Number Button - Coming Soon */}
              <button 
                disabled
                className="w-full flex items-center justify-center gap-3 bg-gray-100 border-2 border-gray-200 text-gray-400 py-4 px-6 rounded-full font-semibold cursor-not-allowed"
              >
                <Phone className="w-5 h-5" />
                Phone Login — Coming Soon
              </button>
            </div>

            {/* Trouble Link */}
            <div className="text-center mb-8">
              <button 
                onClick={() => { setShowAuthModal(false); navigate("/login"); }}
                className="text-[#1C3A7A] underline hover:text-[#FAB710] font-medium"
              >
                Trouble Logging In?
              </button>
            </div>

            {/* Get the App Section */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-[#1C3A7A] mb-4">Get the app!</h3>
              <div className="flex items-center justify-center gap-3">
                <SmartDownloadButton />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-sm w-full animate-in zoom-in-95 duration-200">
            {/* Close button */}
            <button 
              onClick={() => setLightboxImage(null)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Phone Frame */}
            <div className="bg-[#1C3A7A] rounded-[50px] p-3 shadow-2xl">
              {/* Notch */}
              <div className="absolute top-5 left-1/2 -translate-x-1/2 w-24 h-7 bg-[#1C3A7A] rounded-full z-10" />
              
              {/* Screen */}
              <div className="rounded-[40px] overflow-hidden">
                <img 
                  src={lightboxImage.src} 
                  alt={lightboxImage.title}
                  className="w-full h-auto"
                />
              </div>
            </div>
            
            {/* Title */}
            <p className="text-center text-white font-semibold mt-4 text-lg">{lightboxImage.title}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
